import { CheckCircle2, Copy, Play, QrCode, Save, Send, Sparkles, Trash2, Upload } from "lucide-react";
import { type FormEvent, useState } from "react";
import useSWR from "swr";
import { toast } from "sonner";
import {
  PAYMENT_POLL_INTERVAL_MAX_SECONDS,
  PAYMENT_POLL_INTERVAL_MIN_SECONDS,
  type CollectionMode,
  type PublicSettings,
  type TransferLinkLayer,
  type WxChannelStrategy,
} from "@/shared/contracts";
import { apiFetch, jsonBody, swrFetcher } from "@/web/api";
import { PageHeader } from "@/web/components/page-header";
import { Badge } from "@/web/components/ui/badge";
import { Button } from "@/web/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/web/components/ui/card";
import { Input } from "@/web/components/ui/input";
import { Label } from "@/web/components/ui/label";
import { Loading } from "@/web/components/ui/loading";
import { Switch } from "@/web/components/ui/switch";
import { Textarea } from "@/web/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/web/components/ui/tabs";
import { TwoFactorCard } from "@/web/components/two-factor-card";
import { cn } from "@/web/lib/utils";

interface SettingsData extends PublicSettings {
  transfer_user_id: string;
  alipay_public_key: string;
  has_alipay_private_key: boolean;
  has_v1_key: boolean;
  has_v2_platform_private_key: boolean;
  auto_cleanup_enabled: boolean;
  auto_cleanup_retention_hours: number;
  log_retention_days: number;
  scan_audit_retention_days: number;
  wecom_webhook_url: string;
  upstream_enabled: boolean;
  upstream_api_url: string;
  upstream_pid: string;
  has_upstream_key: boolean;
  wx_channel_strategy: WxChannelStrategy;
  wx_channel_cursor: number;
  self_wx_enabled: boolean;
  self_wx_qr_url: string;
  has_self_wx_report_key: boolean;
  self_wx_report_url: string;
  alipay_limit_single_cents: number;
  alipay_limit_daily_cents: number;
  wxpay_limit_single_cents: number;
  wxpay_limit_daily_cents: number;
  usage_today: { alipay: { cents: number; count: number }; wxpay: { cents: number; count: number } };
  aggregate_enabled: boolean;
  aggregate_url: string;
}

/** 分 → 输入框的值；0 显示为空（不限）。 */
function limitToInput(cents: number) {
  return cents > 0 ? String(cents / 100) : "";
}

/** 输入框的值 → 分；空 / 非法 / 非正数一律为 0（不限）。 */
function inputToLimit(value: string) {
  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0 ? Math.round(amount * 100) : 0;
}

function SettingsForm({ initial, refresh }: { initial: SettingsData; refresh: () => Promise<unknown> }) {
  const [mode, setMode] = useState<CollectionMode>(initial.collection_mode);
  const [baseUrl, setBaseUrl] = useState(initial.public_base_url);
  const [transferUserId, setTransferUserId] = useState(initial.transfer_user_id);
  const [transferLinkLayer, setTransferLinkLayer] = useState<TransferLinkLayer>(initial.transfer_link_layer);
  const [appId, setAppId] = useState(initial.alipay_app_id);
  const [endpoint, setEndpoint] = useState(initial.alipay_endpoint);
  const [alipayPublicKey, setAlipayPublicKey] = useState(initial.alipay_public_key);
  const [paymentPollInterval, setPaymentPollInterval] = useState(String(initial.payment_poll_interval_seconds));
  const [v1Enabled, setV1Enabled] = useState(initial.v1_enabled);
  const [v2Enabled, setV2Enabled] = useState(initial.v2_enabled);
  const [autoCleanup, setAutoCleanup] = useState(initial.auto_cleanup_enabled);
  const [logRetention, setLogRetention] = useState(String(initial.log_retention_days));
  const [cleanBusy, setCleanBusy] = useState(false);
  const [wecomWebhook, setWecomWebhook] = useState(initial.wecom_webhook_url);
  const [upstreamEnabled, setUpstreamEnabled] = useState(initial.upstream_enabled);
  const [upstreamUrl, setUpstreamUrl] = useState(initial.upstream_api_url);
  const [upstreamPid, setUpstreamPid] = useState(initial.upstream_pid);
  const [upstreamKey, setUpstreamKey] = useState("");
  const [wxStrategy, setWxStrategy] = useState<WxChannelStrategy>(initial.wx_channel_strategy);
  const [selfWxEnabled, setSelfWxEnabled] = useState(initial.self_wx_enabled);
  const [selfWxQrBusy, setSelfWxQrBusy] = useState(false);
  const [selfWxKey, setSelfWxKey] = useState("");
  const [revealedSelfWxKey, setRevealedSelfWxKey] = useState("");
  const [limitAlipaySingle, setLimitAlipaySingle] = useState(limitToInput(initial.alipay_limit_single_cents));
  const [limitAlipayDaily, setLimitAlipayDaily] = useState(limitToInput(initial.alipay_limit_daily_cents));
  const [limitWxpaySingle, setLimitWxpaySingle] = useState(limitToInput(initial.wxpay_limit_single_cents));
  const [limitWxpayDaily, setLimitWxpayDaily] = useState(limitToInput(initial.wxpay_limit_daily_cents));
  const [aggregateEnabled, setAggregateEnabled] = useState(initial.aggregate_enabled);
  const [testType, setTestType] = useState<"alipay" | "wxpay">("alipay");
  const [testBusy, setTestBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  // 运行日志与缓存清理：可预览可清理量，也可手动立即清理
  const { data: maintenance, mutate: refreshMaintenance } = useSWR<{
    log_retention_days: number;
    scan_audit_retention_days: number;
    cleanable: { scan_runs: number; scan_audits: number; orphan_theme_dirs: number; unreferenced_qr_images: number };
  }>("/admin-api/maintenance", swrFetcher, { refreshInterval: 30_000 });

  async function cleanNow() {
    setCleanBusy(true);
    try {
      const result = await apiFetch<{ cleaned: Record<string, number> }>("/admin-api/maintenance/clean", {
        method: "POST",
        ...jsonBody({}),
      });
      const c = result.cleaned;
      toast.success(
        `已清理：运行日志 ${c.scan_runs} 条、扫描审计 ${c.scan_audits} 条、孤儿主题目录 ${c.orphan_theme_dirs} 个、废弃图片 ${c.unreferenced_qr_images} 张`,
      );
      await refreshMaintenance();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "清理失败");
    } finally {
      setCleanBusy(false);
    }
  }

  /** 自挂收款码上传：先用 canvas 把任意格式转成 PNG（服务端解码只认 PNG）。 */
  async function uploadSelfWxQr(file: File) {
    setSelfWxQrBusy(true);
    try {
      const bitmap = await createImageBitmap(file);
      const canvas = document.createElement("canvas");
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const context = canvas.getContext("2d");
      if (!context) throw new Error("无法读取该图片");
      context.drawImage(bitmap, 0, 0);
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
      if (!blob) throw new Error("图片转 PNG 失败");
      const form = new FormData();
      form.set("file", new File([blob], "self-wx-qr.png", { type: "image/png" }));
      await apiFetch("/admin-api/settings/self-wx-qr", { method: "POST", body: form });
      toast.success("微信收款码已上传");
      await refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "上传失败");
    } finally {
      setSelfWxQrBusy(false);
    }
  }

  async function regenerateSelfWxKey() {
    try {
      const result = await apiFetch<{ key: string }>("/admin-api/keys/self-wx/report/regenerate", { method: "POST" });
      setRevealedSelfWxKey(result.key);
      toast.success("上报密钥已生成，请同步更新挂机程序");
      await refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "生成失败");
    }
  }

  async function revealSelfWxKey() {
    try {
      const result = await apiFetch<{ key: string; url: string }>("/admin-api/keys/self-wx/report/reveal", { method: "POST" });
      setRevealedSelfWxKey(result.key);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "读取失败");
    }
  }

  /** 开一笔假订单：走完整收银台，但不进统计、不占额度。 */
  async function createTestOrder() {
    setTestBusy(true);
    try {
      const result = await apiFetch<{ checkout_url: string; out_trade_no: string }>("/admin-api/orders/test", {
        method: "POST",
        ...jsonBody({ type: testType }),
      });
      window.open(result.checkout_url, "_blank", "noopener");
      toast.success(`测试订单 ${result.out_trade_no} 已创建（不计入统计）`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "创建测试订单失败");
    } finally {
      setTestBusy(false);
    }
  }

  async function clearTestOrders() {
    setTestBusy(true);
    try {
      const result = await apiFetch<{ deleted: number }>("/admin-api/orders/test", { method: "DELETE" });
      toast.success(`已清理 ${result.deleted} 笔测试订单`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "清理失败");
    } finally {
      setTestBusy(false);
    }
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      await apiFetch("/admin-api/settings", {
        method: "PUT",
        ...jsonBody({
          public_base_url: baseUrl,
          collection_mode: mode,
          transfer_user_id: transferUserId,
          transfer_link_layer: transferLinkLayer,
          alipay_app_id: appId,
          alipay_endpoint: endpoint,
          alipay_public_key: alipayPublicKey,
          payment_poll_interval_seconds: Number(paymentPollInterval),
          v1_enabled: v1Enabled,
          v2_enabled: v2Enabled,
          auto_cleanup_enabled: autoCleanup,
          log_retention_days: Number(logRetention),
          wecom_webhook_url: wecomWebhook,
          upstream_enabled: upstreamEnabled,
          upstream_api_url: upstreamUrl,
          upstream_pid: upstreamPid,
          upstream_key: upstreamKey,
          wx_channel_strategy: wxStrategy,
          self_wx_enabled: selfWxEnabled,
          self_wx_report_key: selfWxKey,
          alipay_limit_single_cents: inputToLimit(limitAlipaySingle),
          alipay_limit_daily_cents: inputToLimit(limitAlipayDaily),
          wxpay_limit_single_cents: inputToLimit(limitWxpaySingle),
          wxpay_limit_daily_cents: inputToLimit(limitWxpayDaily),
          aggregate_enabled: aggregateEnabled,
        }),
      });
      toast.success("配置已保存");
      await refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "保存失败");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={save} className="space-y-6">
      <Tabs defaultValue="collect">
        <TabsList>
          <TabsTrigger value="collect">收款配置</TabsTrigger>
          <TabsTrigger value="api">商户接口</TabsTrigger>
          <TabsTrigger value="checkout">通知</TabsTrigger>
          <TabsTrigger value="security">安全</TabsTrigger>
          <TabsTrigger value="data">数据维护</TabsTrigger>
        </TabsList>

        <TabsContent value="collect" className="mt-5">
      <Card>
        <CardHeader><CardTitle>收款方式</CardTitle><CardDescription>经营码金额匹配更稳定；同一时刻的订单会分配不同分位金额。</CardDescription></CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2">
            <button type="button" onClick={() => setMode("business_qr")} className={cn("rounded-lg border p-4 text-left transition-colors", mode === "business_qr" ? "border-primary bg-primary/5" : "hover:bg-foreground/[0.025]")}>
              <div className="flex items-center justify-between"><span className="flex items-center gap-2 font-semibold"><QrCode className="size-4 text-primary" />经营码</span><Badge variant={mode === "business_qr" ? "primary" : "outline"}>{mode === "business_qr" ? "当前使用" : "推荐"}</Badge></div>
              <p className="mt-2 text-sm leading-5 text-muted">上传支付宝经营码，订单实付金额在原价上增加 0.01–0.99 元并精确匹配。</p>
            </button>
            <button type="button" onClick={() => setMode("transfer")} className={cn("rounded-lg border p-4 text-left transition-colors", mode === "transfer" ? "border-primary bg-primary/5" : "hover:bg-foreground/[0.025]")}>
              <div className="flex items-center justify-between"><span className="flex items-center gap-2 font-semibold"><Send className="size-4 text-primary" />转账备注</span><Badge variant={mode === "transfer" ? "primary" : "outline"}>{mode === "transfer" ? "当前使用" : "兼容模式"}</Badge></div>
              <p className="mt-2 text-sm leading-5 text-muted">生成支付宝转账 URI，使用商户订单号作为 memo，同时校验金额与时间。</p>
            </button>
          </div>

          <div className="mt-5 grid gap-5 md:grid-cols-2">
            <div className="space-y-3">
              <Label htmlFor="qr-file">经营码图片</Label>
              {initial.business_qr_url ? <div className="flex items-center gap-3 rounded-md border p-3"><img src={initial.business_qr_url} alt="已上传的支付宝经营码" className="size-20 object-contain" /><div className="min-w-0"><div className="flex items-center gap-2 text-sm font-medium"><CheckCircle2 className="size-4 text-success" />已上传</div><p className="mt-1 truncate text-xs text-muted">{initial.business_qr_url}</p></div></div> : <div className="rounded-md border border-dashed p-5 text-center text-sm text-muted">尚未上传经营码</div>}
              <Input id="qr-file" type="file" accept="image/png,image/jpeg,image/webp" onChange={async (event) => {
                const file = event.target.files?.[0];
                if (!file) return;
                const form = new FormData();
                form.set("file", file);
                try {
                  await apiFetch("/admin-api/settings/qr", { method: "POST", body: form });
                  toast.success("经营码已上传");
                  await refresh();
                } catch (error) { toast.error(error instanceof Error ? error.message : "上传失败"); }
              }} />
              <p className="flex items-start gap-2 text-xs leading-5 text-muted"><Upload className="mt-0.5 size-3.5 shrink-0" />支持 PNG、JPEG、WebP，最大 5MB。</p>
            </div>
            <div className="space-y-3">
              <Label htmlFor="transfer-user">支付宝用户 ID</Label>
              <Input id="transfer-user" inputMode="numeric" value={transferUserId} onChange={(event) => setTransferUserId(event.target.value.trim())} placeholder="2088…" />
              <Label htmlFor="transfer-link-layer">转账链接包裹层级</Label>
              <select
                id="transfer-link-layer"
                value={transferLinkLayer}
                onChange={(event) => setTransferLinkLayer(Number(event.target.value) as TransferLinkLayer)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs outline-none transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
              >
                <option value={1}>第 1 层 · 原始 alipays Scheme</option>
                <option value={2}>第 2 层 · 单层支付宝 HTTPS（已验证可用）</option>
                <option value={3}>第 3 层 · 外层 alipays Scheme</option>
              </select>
              <p className="text-xs leading-5 text-muted">配置会同时作用于收银台二维码、V1 qrcode 和 V2 pay_info；更换后新生成或重新打开的支付页面立即生效。</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>收款限额</CardTitle>
          <CardDescription>
            按支付方式限制能收多少：单笔超上限直接拒单，当日累计到达上限后当天不再收。0 或留空表示不限。
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3 rounded-md border p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm font-medium">支付宝</div>
              <span className="text-xs tabular-nums text-muted">
                今日已占用 ¥{((initial.usage_today?.alipay?.cents ?? 0) / 100).toFixed(2)} · {initial.usage_today?.alipay?.count ?? 0} 单
              </span>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="alipay-limit-single">单笔上限（元）</Label>
                <Input id="alipay-limit-single" inputMode="decimal" value={limitAlipaySingle} onChange={(event) => setLimitAlipaySingle(event.target.value.replace(/[^\d.]/g, ""))} placeholder="不限" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="alipay-limit-daily">每日上限（元）</Label>
                <Input id="alipay-limit-daily" inputMode="decimal" value={limitAlipayDaily} onChange={(event) => setLimitAlipayDaily(event.target.value.replace(/[^\d.]/g, ""))} placeholder="不限" />
              </div>
            </div>
          </div>

          <div className="space-y-3 rounded-md border p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm font-medium">微信</div>
              <span className="text-xs tabular-nums text-muted">
                今日已占用 ¥{((initial.usage_today?.wxpay?.cents ?? 0) / 100).toFixed(2)} · {initial.usage_today?.wxpay?.count ?? 0} 单
              </span>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="wxpay-limit-single">单笔上限（元）</Label>
                <Input id="wxpay-limit-single" inputMode="decimal" value={limitWxpaySingle} onChange={(event) => setLimitWxpaySingle(event.target.value.replace(/[^\d.]/g, ""))} placeholder="不限" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wxpay-limit-daily">每日上限（元）</Label>
                <Input id="wxpay-limit-daily" inputMode="decimal" value={limitWxpayDaily} onChange={(event) => setLimitWxpayDaily(event.target.value.replace(/[^\d.]/g, ""))} placeholder="不限" />
              </div>
            </div>
          </div>

          <p className="text-xs leading-5 text-muted">
            「今日已占用」含已收款与仍在等待支付的订单（避免并发把额度打爆），超时未付的不计入；按北京时间自然日重置。
            修改后点底部「保存全部配置」生效。
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>聚合支付</CardTitle>
          <CardDescription>
            开启后这个网址可以直接发给别人：对方自己输入金额、选支付方式，提交后进收银台。订单计入正常收款统计。
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-4 rounded-md border p-4">
            <div>
              <div className="text-sm font-medium">启用聚合支付</div>
              <p className="mt-1 text-xs leading-5 text-muted">公开页面，拿到链接的人都能自助下单；已按 IP 限流（每分钟 5 单）。</p>
            </div>
            <Switch checked={aggregateEnabled} onCheckedChange={setAggregateEnabled} aria-label="启用聚合支付" />
          </div>
          <div className="space-y-2">
            <Label>支付链接</Label>
            <div className="flex flex-wrap items-center gap-2">
              <Input readOnly value={initial.aggregate_url} className="min-w-0 flex-1 font-mono text-xs" />
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  void navigator.clipboard.writeText(initial.aggregate_url);
                  toast.success("链接已复制");
                }}
              >
                <Copy />复制
              </Button>
            </div>
            <p className="text-xs leading-5 text-muted">想限制对方能付多少，用上面的「收款限额」控制（微信 / 支付宝各一组）。</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>支付测试</CardTitle>
          <CardDescription>
            开一笔假订单走完整收银台（二维码是真的、扫了能真付款），但它不进统计、不占收款额度，验完可一键清理。
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-2">
              <Label htmlFor="test-type">支付方式</Label>
              <select
                id="test-type"
                value={testType}
                onChange={(event) => setTestType(event.target.value as "alipay" | "wxpay")}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs outline-none transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
              >
                <option value="alipay">支付宝</option>
                <option value="wxpay">微信</option>
              </select>
            </div>
            <Button type="button" disabled={testBusy} onClick={() => void createTestOrder()}>
              <Play />创建测试订单
            </Button>
            <Button type="button" variant="outline" disabled={testBusy} onClick={() => void clearTestOrders()}>
              <Trash2 />清理测试订单
            </Button>
          </div>
          <p className="rounded-md bg-foreground/[0.035] px-4 py-3 text-xs leading-5 text-muted">
            测试订单金额固定 0.01 元，会新开一个标签页打开收银台，你可以真的扫码付款来验证整条链路。
            因为它打了测试标记，所以不会出现在收款统计里，也不会占用金额池。
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>支付宝 V3 账单接口</CardTitle><CardDescription>使用官方 Node SDK 发起 GET /v3/alipay/data/bill/accountlog/query，并校验响应签名。</CardDescription></CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-5 md:grid-cols-2">
            <div className="space-y-2"><Label htmlFor="app-id">应用 ID</Label><Input id="app-id" value={appId} onChange={(event) => setAppId(event.target.value.trim())} placeholder="支付宝开放平台 AppId" /></div>
            <div className="space-y-2"><Label htmlFor="endpoint">V3 Endpoint</Label><Input id="endpoint" type="url" value={endpoint} onChange={(event) => setEndpoint(event.target.value)} /></div>
          </div>
          <div className="space-y-2"><Label htmlFor="alipay-public">支付宝公钥</Label><Textarea id="alipay-public" rows={7} value={alipayPublicKey} onChange={(event) => setAlipayPublicKey(event.target.value)} placeholder="-----BEGIN PUBLIC KEY-----" /><p className="text-xs text-muted">这里填写支付宝公钥，不是应用公钥。应用私钥在密钥中心生成或导入。</p></div>
          <div className="max-w-sm space-y-2">
            <Label htmlFor="payment-poll-interval">支付轮询间隔（秒）</Label>
            <Input
              id="payment-poll-interval"
              type="number"
              inputMode="numeric"
              min={PAYMENT_POLL_INTERVAL_MIN_SECONDS}
              max={PAYMENT_POLL_INTERVAL_MAX_SECONDS}
              step={1}
              required
              value={paymentPollInterval}
              onChange={(event) => setPaymentPollInterval(event.target.value)}
            />
            <p className="text-xs leading-5 text-muted">允许 1–60 秒，同时控制服务端支付宝扫账、收银台状态查询以及易支付订单查询的刷新节流；保存后无需重启。</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant={initial.alipay_configured ? "success" : "danger"}>{initial.alipay_configured ? "凭据完整" : "凭据不完整"}</Badge>
            <Button type="button" variant="outline" disabled={!initial.alipay_configured} onClick={async () => {
              try { const result = await apiFetch<{ trace_id: string }>("/admin-api/alipay/test", { method: "POST" }); toast.success(`连接成功，Trace ${result.trace_id || "—"}`); }
              catch (error) { toast.error(error instanceof Error ? error.message : "连接失败"); }
            }}>测试连接</Button>
          </div>
        </CardContent>
      </Card>

        </TabsContent>

        <TabsContent value="api" className="mt-5">
      <Card>
        <CardHeader><CardTitle>公开地址</CardTitle><CardDescription>修改域名后请重新上传经营码，以更新图片绝对地址。</CardDescription></CardHeader>
        <CardContent><div className="space-y-2"><Label htmlFor="public-base">PUBLIC_BASE_URL</Label><Input id="public-base" type="url" value={baseUrl} onChange={(event) => setBaseUrl(event.target.value)} /></div></CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>上游通道（微信支付）</CardTitle>
          <CardDescription>开启后，商户传 type=wxpay 时转发给上游易支付，由上游提供微信收款；type=alipay 仍走本地经营码。</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-4 rounded-md border p-4">
            <div>
              <div className="text-sm font-medium">启用上游通道</div>
              <p className="mt-1 text-xs leading-5 text-muted">未启用时，type=wxpay 的请求会直接返回错误，不会静默失败。</p>
            </div>
            <Switch checked={upstreamEnabled} onCheckedChange={setUpstreamEnabled} aria-label="启用上游通道" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="upstream-url">上游接口地址</Label>
            <Input id="upstream-url" type="url" placeholder="https://upstream.example.com/" value={upstreamUrl} onChange={(event) => setUpstreamUrl(event.target.value)} />
            <p className="text-xs leading-5 text-muted">填上游站点根地址即可，系统会自动拼接 mapi.php。</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="upstream-pid">上游商户 ID</Label>
              <Input id="upstream-pid" value={upstreamPid} onChange={(event) => setUpstreamPid(event.target.value)} placeholder="1000" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="upstream-key">上游商户密钥</Label>
              <Input
                id="upstream-key"
                type="password"
                value={upstreamKey}
                onChange={(event) => setUpstreamKey(event.target.value)}
                placeholder={initial.has_upstream_key ? "已配置，留空则不修改" : "请输入上游密钥"}
              />
            </div>
          </div>
          <p className="rounded-md bg-foreground/[0.035] px-4 py-3 text-xs leading-5 text-muted">
            回调地址会自动使用本站的 <code className="font-mono">/notify/upstream</code>，
            无需在上游手动填写。上游异步通知会验签、比对金额后标记订单已支付，并转发给商户。
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>微信支付分流策略</CardTitle>
          <CardDescription>
            商户传 type=wxpay 时走哪条通道。选中的通道不可用（未配置 / 上游超时）会自动回落到另一条，两条都不可用才报错。
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2">
            {([
              ["upstream_only", "仅上游易支付", "全部微信支付转给上游，与你原来的行为完全一致。", "默认"],
              ["self_only", "仅微信自挂", "全部走自己挂的收款码，靠挂机程序上报确认到账。", ""],
              ["random", "随机", "两条都可用时，逐单随机选一条。", ""],
              ["round_robin", "轮流", "两条都可用时逐单交替（一单换一次）。", ""],
            ] as const).map(([value, title, description, badge]) => (
              <button key={value} type="button" onClick={() => setWxStrategy(value)} className={cn("rounded-lg border p-4 text-left transition-colors", wxStrategy === value ? "border-primary bg-primary/5" : "hover:bg-foreground/[0.025]")}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold">{title}</span>
                  {value === wxStrategy ? <Badge variant="primary">当前使用</Badge> : badge ? <Badge variant="outline">{badge}</Badge> : null}
                </div>
                <p className="mt-2 text-sm leading-5 text-muted">{description}</p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>微信自挂通道</CardTitle>
          <CardDescription>
            上传一张自己的微信收款码，客户扫它付款；到账由挂机程序调上报接口确认。启用后按上面的策略与上游分流。
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-4 rounded-md border p-4">
            <div>
              <div className="text-sm font-medium">启用微信自挂</div>
              <p className="mt-1 text-xs leading-5 text-muted">必须同时上传收款码；两者齐备才会被分流选中。</p>
            </div>
            <Switch checked={selfWxEnabled} onCheckedChange={setSelfWxEnabled} aria-label="启用微信自挂" />
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            <div className="space-y-3">
              <Label htmlFor="self-wx-qr">微信收款码（固定一张）</Label>
              {initial.self_wx_qr_url ? (
                <div className="flex items-center gap-3 rounded-md border p-3">
                  <img src={initial.self_wx_qr_url} alt="已上传的微信收款码" className="size-20 object-contain" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm font-medium"><CheckCircle2 className="size-4 text-success" />已上传</div>
                    <p className="mt-1 truncate text-xs text-muted">{initial.self_wx_qr_url}</p>
                  </div>
                </div>
              ) : (
                <div className="rounded-md border border-dashed p-5 text-center text-sm text-muted">尚未上传收款码</div>
              )}
              <Input
                id="self-wx-qr"
                type="file"
                accept="image/*"
                disabled={selfWxQrBusy}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void uploadSelfWxQr(file);
                  event.target.value = "";
                }}
              />
              <p className="flex items-start gap-2 text-xs leading-5 text-muted">
                <Upload className="mt-0.5 size-3.5 shrink-0" />
                支持任意图片格式（上传前自动转 PNG），最大 5MB。收银台会把这张码解码后重画，观感与其它二维码一致。
              </p>
            </div>

            <div className="space-y-3">
              <Label htmlFor="self-wx-report-key">上报密钥</Label>
              <Input
                id="self-wx-report-key"
                type="password"
                value={selfWxKey}
                onChange={(event) => setSelfWxKey(event.target.value)}
                placeholder={initial.has_self_wx_report_key ? "已配置，留空则不修改" : "尚未生成，可点右侧按钮生成"}
              />
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" onClick={() => void regenerateSelfWxKey()}>
                  <Sparkles />{initial.has_self_wx_report_key ? "重新生成" : "生成密钥"}
                </Button>
                {initial.has_self_wx_report_key ? (
                  <Button type="button" variant="outline" onClick={() => void revealSelfWxKey()}>查看当前密钥</Button>
                ) : null}
              </div>
              {revealedSelfWxKey ? (
                <p className="break-all rounded-md bg-foreground/[0.035] px-3 py-2 font-mono text-xs">{revealedSelfWxKey}</p>
              ) : null}
            </div>
          </div>

          <div className="rounded-md bg-foreground/[0.035] px-4 py-3 text-xs leading-6 text-muted">
            <p className="font-medium text-foreground">挂机程序上报接口（GET 或 POST 均可）</p>
            <p className="mt-1">地址：<code className="break-all font-mono">{initial.self_wx_report_url || "https://你的域名/report/self-wx"}</code></p>
            <p>
              参数：<code className="font-mono">key</code>（上报密钥）、<code className="font-mono">money</code>（实收金额，元）；
              可选 <code className="font-mono">out_trade_no</code> 或 <code className="font-mono">trade_no</code>（直接指定订单）、
              <code className="font-mono">time</code>（到账时间）、<code className="font-mono">buyer</code>（付款方）。
            </p>
            <p>
              不想自己算金额也行：把收款通知原文放进 <code className="font-mono">msg</code> /
              <code className="font-mono">content</code> / <code className="font-mono">text</code> 字段
              （手机端的通知转发 App 直接就能发），服务端会自己识别出金额。
            </p>
            <pre className="mt-1.5 overflow-x-auto whitespace-pre-wrap break-all rounded bg-foreground/[0.06] p-2 font-mono text-[11px] leading-5">
              {`curl -X POST "${initial.self_wx_report_url || "https://你的域名/report/self-wx"}" -d "key=${revealedSelfWxKey || "上报密钥"}" -d "money=10.01"`}
            </pre>
            <p className="mt-1">不传订单号时按金额匹配（自挂订单金额唯一，不会认错）；认账窗口为下单后 10 分钟，超时需到订单页手动补单。</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>商户 API</CardTitle><CardDescription>可分别停用旧版 MD5 和新版 RSA 接口；已有订单的通知仍按创建时的版本签名。</CardDescription></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-4 rounded-md border p-4"><div><div className="text-sm font-medium">V1 MD5</div><p className="mt-1 text-xs text-muted">submit.php、mapi.php 与 api.php 查询。</p></div><Switch checked={v1Enabled} onCheckedChange={setV1Enabled} aria-label="启用 V1 API" /></div>
          <div className="flex items-center justify-between gap-4 rounded-md border p-4"><div><div className="text-sm font-medium">V2 RSA</div><p className="mt-1 text-xs text-muted">支付、订单查询与商户信息接口。</p></div><Switch checked={v2Enabled} onCheckedChange={setV2Enabled} aria-label="启用 V2 API" /></div>
        </CardContent>
      </Card>

        </TabsContent>

        <TabsContent value="checkout" className="mt-5">
      <Card>
        <CardHeader>
          <CardTitle>支付通知</CardTitle>
          <CardDescription>每笔成功收款都会向企业微信机器人推送 Markdown 消息；留空则关闭通知。</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="wecom-webhook">企业微信群机器人 Webhook</Label>
            <Input
              id="wecom-webhook"
              type="url"
              value={wecomWebhook}
              onChange={(event) => setWecomWebhook(event.target.value)}
              placeholder="https://qyapi.weixin.qq.com/cgi-bin/webhook/send?key=..."
            />
            <p className="text-xs leading-5 text-muted">
              在群聊右上角「群机器人 → 添加机器人」即可拿到该地址。
              自动匹配和手动补单都会推送，内容包含金额、商品、单号与到账时间。
            </p>
          </div>
        </CardContent>
      </Card>

        </TabsContent>

        <TabsContent value="security" className="mt-5">
          <TwoFactorCard />
        </TabsContent>

        <TabsContent value="data" className="mt-5">
      <Card>
        <CardHeader>
          <CardTitle>订单留存</CardTitle>
          <CardDescription>开启后，未收到款项的失败订单会在 {initial.auto_cleanup_retention_hours} 小时后自动删除；已支付订单始终保留。</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4 rounded-md border p-4">
            <div>
              <div className="text-sm font-medium">自动清理失败订单</div>
              <p className="mt-1 text-xs text-muted">后台每小时检查一次，超过留存期的未支付订单会被删除。</p>
            </div>
            <Switch checked={autoCleanup} onCheckedChange={setAutoCleanup} aria-label="启用自动清理" />
          </div>
        </CardContent>
      </Card>

      <Card className="mt-5">
        <CardHeader>
          <CardTitle>运行日志与缓存</CardTitle>
          <CardDescription>
            后台每小时自动清理一次，也可以随时手动点下面按钮清理。
            已支付订单、支付流水，以及除「扫描」之外的审计记录（删单/退款/补单/登录/改配置）永不删除。
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="log-retention">运行日志留存天数</Label>
              <Input
                id="log-retention"
                inputMode="numeric"
                value={logRetention}
                onChange={(event) => setLogRetention(event.target.value.replace(/[^\d]/g, ""))}
                placeholder="7"
              />
              <p className="text-xs leading-5 text-muted">
                扫描记录超过这个天数会被清掉，取值 1–180。修改后需点底部「保存全部配置」生效。
              </p>
            </div>
            <div className="space-y-2">
              <Label>当前可清理</Label>
              <div className="rounded-md border p-3 text-xs leading-6 text-muted">
                <div>运行日志：<strong className="text-foreground">{maintenance?.cleanable.scan_runs ?? 0}</strong> 条</div>
                <div>扫描审计：<strong className="text-foreground">{maintenance?.cleanable.scan_audits ?? 0}</strong> 条（固定留 {maintenance?.scan_audit_retention_days ?? 30} 天）</div>
                <div>孤儿主题目录：<strong className="text-foreground">{maintenance?.cleanable.orphan_theme_dirs ?? 0}</strong> 个</div>
                <div>废弃经营码图片：<strong className="text-foreground">{maintenance?.cleanable.unreferenced_qr_images ?? 0}</strong> 张</div>
              </div>
              <Button type="button" variant="outline" disabled={cleanBusy} onClick={() => void cleanNow()}>
                <Sparkles />{cleanBusy ? "正在清理…" : "立即清理"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

        </TabsContent>
      </Tabs>

      <div className="flex justify-end"><Button size="lg" disabled={saving}>{saving ? "正在保存…" : "保存全部配置"}<Save /></Button></div>
    </form>
  );
}

export function SettingsPage() {
  const { data, isLoading, mutate } = useSWR<SettingsData>("/admin-api/settings", swrFetcher);
  if (isLoading || !data) return <Loading label="正在读取配置" />;
  return (
    <>
      <PageHeader title="收款配置" description="切换收款方式、配置支付宝 V3 和控制商户 API 开关。" />
      <SettingsForm initial={data} refresh={mutate} />
    </>
  );
}
